import React, { useState, useEffect, useMemo } from 'react';
import { formatVisits, formatVisitors } from '../utils/formatMetrics';
import './Modal.css';

const LoadAnalysisModal = ({ isOpen, onClose, onLoad, onDelete, analyses, loading }) => {
  const [selectedId, setSelectedId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setSelectedId(null);
      setDeleteConfirm(null);
      setSearchQuery('');
    }
  }, [isOpen]);

  const handleLoad = () => {
    if (selectedId) {
      onLoad(selectedId);
    }
  };

  const handleDelete = (id) => {
    if (deleteConfirm === id) {
      onDelete(id);
      setDeleteConfirm(null);
    } else {
      setDeleteConfirm(id);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Filter analyses by search query
  const filteredAnalyses = useMemo(() => {
    if (!searchQuery.trim()) return analyses;
    const query = searchQuery.toLowerCase();
    return analyses.filter(analysis =>
      analysis.analysis_name.toLowerCase().includes(query)
    );
  }, [analyses, searchQuery]);

  // Export to CSV
  const handleExportCSV = () => {
    const headers = ['Name', 'Date/Time', 'Sample Size', 'Origins', 'Visits', 'Visitors'];
    const csvRows = [headers.join(',')];

    analyses.forEach(analysis => {
      const row = [
        `"${analysis.analysis_name || ''}"`,
        `"${formatDate(analysis.created_at)}"`,
        analysis.total_devices || 0, // Raw sample size (not extrapolated)
        analysis.total_origins || 0,
        `"${formatVisits(analysis.total_devices || 0)}"`, // Extrapolated visits with ranges
        `"${formatVisitors(analysis.total_uniques || 0)}"` // Extrapolated visitors with ranges
      ];
      csvRows.push(row.join(','));
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `saved_analyses_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px' }}>
        <div className="modal-header">
          <h2>Load Saved Analysis</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          {analyses.length === 0 ? (
            <div className="empty-state">
              <p>No saved analyses found.</p>
              <p className="empty-state-hint">Create an analysis and click "Save Analysis" to save it.</p>
            </div>
          ) : (
            <>
              {/* Search input */}
              <div style={{ marginBottom: '1rem' }}>
                <input
                  type="text"
                  placeholder="Search by name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    borderRadius: '4px',
                    border: '1px solid #ddd',
                    backgroundColor: '#fff',
                    color: '#2c3e50'
                  }}
                />
              </div>

              {/* Analyses list */}
              <div className="analyses-list" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {filteredAnalyses.length === 0 ? (
                  <div className="empty-state">
                    <p>No analyses match your search.</p>
                  </div>
                ) : (
                  filteredAnalyses.map((analysis) => (
                    <div
                      key={analysis.id}
                      className={`analysis-item ${selectedId === analysis.id ? 'selected' : ''}`}
                      onClick={() => setSelectedId(analysis.id)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '0.75rem',
                        gap: '1rem',
                        cursor: 'pointer',
                        borderRadius: '4px',
                        border: selectedId === analysis.id ? '2px solid #667eea' : '1px solid #34495e',
                        backgroundColor: selectedId === analysis.id ? 'rgba(102, 126, 234, 0.1)' : '#2c3e50',
                        marginBottom: '0.5rem'
                      }}
                    >
                      <div style={{ flex: '2', minWidth: '150px', fontWeight: 'bold', color: '#fff' }}>
                        {analysis.analysis_name}
                      </div>
                      <div style={{ flex: '1.5', minWidth: '120px', fontSize: '0.85rem', color: '#fff' }}>
                        {formatDate(analysis.created_at)}
                      </div>
                      <div style={{ flex: '0.8', minWidth: '80px', fontSize: '0.85rem', textAlign: 'center', color: '#fff' }}>
                        {analysis.total_devices ? analysis.total_devices.toLocaleString() : 0}
                        <div style={{ fontSize: '0.7rem', color: '#95a5a6' }}>sample size</div>
                      </div>
                      <div style={{ flex: '0.8', minWidth: '80px', fontSize: '0.85rem', textAlign: 'center', color: '#fff' }}>
                        {analysis.total_origins || 0}
                        <div style={{ fontSize: '0.7rem', color: '#95a5a6' }}>origins</div>
                      </div>
                      <div style={{ flex: '0.8', minWidth: '80px', fontSize: '0.85rem', textAlign: 'center', color: '#fff' }}>
                        {formatVisits(analysis.total_devices || 0)}
                        <div style={{ fontSize: '0.7rem', color: '#95a5a6' }}>visits</div>
                      </div>
                      <div style={{ flex: '0.8', minWidth: '80px', fontSize: '0.85rem', textAlign: 'center', color: '#fff' }}>
                        {formatVisitors(analysis.total_uniques || 0)}
                        <div style={{ fontSize: '0.7rem', color: '#95a5a6' }}>visitors</div>
                      </div>
                      <button
                        className={`btn-delete-small ${deleteConfirm === analysis.id ? 'confirm' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(analysis.id);
                        }}
                        disabled={loading}
                        title={deleteConfirm === analysis.id ? 'Click again to confirm' : 'Delete'}
                        style={{
                          backgroundColor: 'transparent',
                          border: 'none',
                          color: '#fff',
                          fontSize: '1.2rem',
                          cursor: 'pointer',
                          padding: '0.25rem 0.5rem',
                          flex: '0 0 auto'
                        }}
                      >
                        {deleteConfirm === analysis.id ? '✓' : '🗑'}
                      </button>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          <button
            type="button"
            className="btn-secondary"
            onClick={handleExportCSV}
            disabled={loading || analyses.length === 0}
          >
            Export CSV
          </button>
          <button
            type="button"
            className="btn-secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={handleLoad}
            disabled={!selectedId || loading}
          >
            {loading ? 'Loading...' : 'Load Analysis'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LoadAnalysisModal;
